import {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'

import {
    useMutation,
} from '@tanstack/react-query'

import preCheckDoNotAgreeMutation from './services/pre-check-do-not-agree'

import TextInput from '../../../../Components/TextInput'
import formatDateDynamic from '../../../../helpers/format-date-dynamic'
import Overlay from '../../../../Components/Overlay'
import DoNotAgreeConfirmationDialog from './Components/DoNotAgreeConfirmationDialog'

const nameValidator = (val) => val.trim().length >= 2

const SignatureAndIdentifier = forwardRef(({ data, company, refToPrint, handleNext, refetch, formKey }, ref) => {

    const [lastFourSSN, setLastFourSSN] = useState(data?.lastFourSSN ?? '')
    const [confirmLastFourSSN, setConfirmLastFourSSN] = useState(data?.lastFourSSN ?? '')
    const [agreeFinal, setAgreeFinal] = useState(data?.agreeFinal ?? null)
    const [error, setError] = useState('')
    const [showDoNotAgreeDialog, setShowDoNotAgreeDialog] = useState(false)

    const todayDate = formatDateDynamic()
    const nameRef = useRef()

    const {
        error: preCheckDoNotAgreeError,
        isPending: preCheckDoNotAgreeIsPending,
        mutate: preCheckDoNotAgree,
    } = useMutation({
        mutationFn: preCheckDoNotAgreeMutation,
        onSuccess: async (d) => {
            setShowDoNotAgreeDialog(false)
            refetch()
        }
    })

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            let isValid = true
            const nameData = nameRef.current.validateAndGetData()

            if (!nameData.isValid) {
                setError('Please enter your full legal name')
                isValid = false
            } else if (lastFourSSN.length !== 4) {
                setError('Please enter the last 4 digits of your SSN')
                isValid = false
            } else if (confirmLastFourSSN !== lastFourSSN) {
                setError('The last 4 digits of your SSN do not match')
                isValid = false
            } else if (agreeFinal === null) {
                setError('Please select whether you agree or do not agree')
                isValid = false
            } else if (agreeFinal === false) {
                setError(
                    `Contact ${company?.name ? `${company.name} (the "Company")` : 'the Company'} Recruitment team${company?.contactEmail ? ` at ${company.contactEmail}` : ''} to proceed`
                )
                isValid = false
            }

            isValid = isValid && nameData.isValid

            if (isValid) {
                setError('')
            }

            return {
                isValid,
                value: {
                    fullName: nameData.value,
                    lastFourSSN,
                    date: todayDate,
                    agreeFinal,
                },
            }
        },
    }))

    return (
        <div ref={refToPrint} className="flex items-center justify-center px-4 py-10">
            <div className="max-w-3xl w-full bg-white shadow-xl rounded-2xl p-8 space-y-6">

                <div className="space-y-2">
                    <h1 className="text-2xl font-bold text-gray-800">Signature &amp; Identifier</h1>
                </div>

                <div className="space-y-4">
                    <h2 className="font-semibold text-gray-800">Electronic Signature Authorization</h2>
                    <p className="text-gray-700 text-sm sm:text-base">
                        By typing my full legal name and the last four digits of my SSN, I am signing this
                        authorization electronically. I acknowledge that my electronic signature has the same
                        legal validity as a handwritten signature. I certify that I have read and understand the
                        disclosures provided in <strong>"Legal Disclosures &amp; Consent"</strong>, and I
                        authorize <strong>VerifiedID LLC</strong> to conduct this identity and employability
                        validation.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                        <div>
                            <TextInput
                                title="Full Legal Name"
                                ref={nameRef}
                                data={data?.fullName ?? ''}
                                placeholder="Enter your full legal name"
                                isMandatory={true}
                                validator={nameValidator}
                                disabled={false}
                            />
                        </div>

                        <div className="my-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Last 4 digits of SSN <span className="text-red-500">*</span>
                            </label>
                            <input
                                value={lastFourSSN}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '').slice(0, 4)
                                    setLastFourSSN(val)
                                    setError('')
                                }}
                                maxLength={4}
                                inputMode="numeric"
                                className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2 text-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="1234"
                            />
                        </div>

                        <div className="my-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Confirm Last 4 digits of SSN <span className="text-red-500">*</span>
                            </label>
                            <input
                                value={confirmLastFourSSN}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '').slice(0, 4)
                                    setConfirmLastFourSSN(val)
                                    setError('')
                                }}
                                maxLength={4}
                                inputMode="numeric"
                                className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2 text-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="1234"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
                            <input
                                value={todayDate}
                                readOnly
                                className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2 bg-gray-100 text-gray-700"
                            />
                        </div>
                    </div>

                    <div className="space-y-2 pt-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="radio"
                                name="agreeFinal"
                                checked={agreeFinal === true}
                                onChange={() => { setAgreeFinal(true); setError('') }}
                            />
                            <span className="text-gray-700 text-sm font-medium">I AGREE</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="radio"
                                name="agreeFinal"
                                checked={agreeFinal === false}
                                onChange={() => { setAgreeFinal(false); setError('') }}
                            />
                            <span className="text-gray-700 text-sm font-medium">I DO NOT AGREE</span>
                        </label>
                    </div>

                    {agreeFinal === false && (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                            Contact{' '}
                            <strong>{company?.name ? `${company.name} (the "Company")` : 'the Company'}</strong>{' '}
                            Recruitment team{company?.contactEmail ? (
                                <> at <a href={`mailto:${company.contactEmail}`} className="underline">{company.contactEmail}</a></>
                            ) : ''}{' '}
                            to proceed.
                        </p>
                    )}
                </div>

                {error && agreeFinal !== false && (
                    <p className="text-sm text-red-600">{error}</p>
                )}

                <div className="text-center">
                    <button
                        onClick={() => {
                            if (agreeFinal === false) {
                                // Declining doesn't submit the signature — the do-not-agree
                                // call only needs the form key — so the name/SSN fields are
                                // not required to proceed here.
                                setError('')
                                setShowDoNotAgreeDialog(true)
                            } else {
                                handleNext()
                            }
                        }}
                        className={`px-6 py-3 rounded-xl text-white font-semibold text-lg shadow-md transition-all ${agreeFinal === false
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                    >
                        {agreeFinal === false ? 'Decline Authorization' : 'Authorize and Start Camera Session'}
                    </button>
                </div>

            </div>

            <DoNotAgreeConfirmationDialog
                isOpen={showDoNotAgreeDialog}
                onConfirm={() => {
                    preCheckDoNotAgree({
                        key: formKey,
                    })
                }}
                onCancel={() => setShowDoNotAgreeDialog(false)}
                company={company}
                isPending={preCheckDoNotAgreeIsPending}
            />

            {
                preCheckDoNotAgreeIsPending && (
                    <Overlay />
                )
            }

        </div>
    )

})

export default SignatureAndIdentifier
